/**
 * Company profile drawer — opens from a Company card and shows the company's
 * contacts scoped to that company, plus quick affordances:
 *
 *  - Searchable contact combobox that lists **only** contacts of this company.
 *    Selecting a contact scrolls to that row in the table below (soft UX
 *    hint; primary use-case is finding a contact fast in a long list).
 *  - "+ Add contact" button pre-fills company_id so the newly-created row
 *    is guaranteed to be tied to this company.
 *  - Inline table of contacts with the essentials (name, title, email, mobile).
 *
 * We load contacts scoped by `company_id` on open (server-side filter), so
 * the dialog stays snappy even when the workspace has thousands of contacts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Globe, MapPin, Users, Plus, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { ContactCombobox } from "@/components/EntityComboboxes";
import { QuickAddContactDialog } from "@/components/QuickAddDialogs";

export default function CompanyProfileDialog({ company, open, onOpenChange }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(""); // contact id selected in combobox
  const [quickOpen, setQuickOpen] = useState(false);
  const qc = useQueryClient();

  const load = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", { params: { company_id: company.id } });
      setContacts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [company?.id]);

  useEffect(() => {
    if (open) { setHighlighted(""); load(); }
  }, [open, load]);

  const sorted = useMemo(
    () => [...contacts].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    ),
    [contacts]
  );

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid={`company-profile-${company.id}`}>
        <DialogHeader>
          <div className="text-xs text-muted-foreground uppercase tracking-[0.2em]">
            {company.industry || "Company"}
          </div>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="text-2xl">
            {company.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Company profile with contact list and quick-add.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground -mt-2">
          {company.website && (<div className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" strokeWidth={1.75} />{company.website}</div>)}
          {company.location && (<div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />{company.location}</div>)}
          {company.size && (<div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" strokeWidth={1.75} />{company.size} employees</div>)}
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          <div className="text-sm font-medium">
            Contacts <span className="text-muted-foreground">({contacts.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <ContactCombobox
              contacts={contacts}
              companyId={company.id}
              value={highlighted}
              onChange={setHighlighted}
              placeholder="Find contact…"
              searchPlaceholder="Search this company's contacts…"
              testId="company-profile-contact-search"
            />
            <Button
              type="button"
              onClick={() => setQuickOpen(true)}
              data-testid="company-profile-add-contact"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add contact
            </Button>
          </div>
        </div>

        <div className="card-flat overflow-x-auto max-h-[45vh] overflow-y-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="bg-secondary/60 border-b border-border sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Title</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Email</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Mobile</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                    No contacts for this company yet. Use <b>Add contact</b> to create one.
                  </td>
                </tr>
              )}
              {sorted.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`company-profile-contact-row-${c.id}`}
                  className={`transition-colors duration-150 ${highlighted === c.id ? "bg-primary/10" : "hover:bg-accent/40"}`}
                >
                  <td className="px-3 py-2.5 font-medium">{c.name}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{c.title || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {c.email
                      ? (<span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" strokeWidth={1.75} />{c.email}</span>)
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {c.mobile
                      ? (<span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" strokeWidth={1.75} />{c.mobile}</span>)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <QuickAddContactDialog
          open={quickOpen}
          onOpenChange={setQuickOpen}
          companies={[company]}          // only this company is a valid target
          defaultCompanyId={company.id}
          lockCompany                    // don't let user re-target
          onCreated={(newContact) => {
            setContacts((prev) => [...prev, newContact]);
            setHighlighted(newContact.id);
            qc.invalidateQueries({ queryKey: ["contacts"] });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
